import { c as _c } from "react/compiler-runtime";
import { DateRangePicker, Popover, getDate } from '@systemeio/ui';
import { useContactSurveys } from 'modules/contacts/contact-page/hooks/use-contact-surveys';
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface';
import { ContactSurveysContentInterface, ContactSurveysInterface } from 'modules/contacts/contact-page/types/contact-surveys-interface';
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface';
import React, { useEffect } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { OrderEnum } from 'shared/types/pagination-interface';
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates';
import { CalendarBookingIcon } from '@systemeio/ui/icons';
import { pageTypeTitlesLocoKeys } from 'shared/enums/page-type-enum';
interface ContactSurveysProps {
  contactId: ContactId;
  contact?: ContactInterface;
}
const defaultFilters: ContactPageFilterInterface = {
  date: [null, null]
};
const hasSurveyContent = (content: ContactSurveysInterface['content']): content is ContactSurveysContentInterface[] => !Array.isArray(content[0]);
const ContactSurveys = t0 => {
  const $ = _c(72);
  const {
    contactId,
    contact
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters, true);
  const t1 = filter.date[0] !== null;
  let t2;
  if ($[0] !== contactId || $[1] !== filter || $[2] !== limit || $[3] !== pagination || $[4] !== t1) {
    t2 = {
      contactId,
      limit,
      pagination,
      filter,
      shouldFetch: t1
    };
    $[0] = contactId;
    $[1] = filter;
    $[2] = limit;
    $[3] = pagination;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    contactSurveys
  } = useContactSurveys(t2);
  let t3;
  if ($[6] !== contact || $[7] !== setFilter || $[8] !== user?.timezone) {
    t3 = () => {
      if (contact) {
        setFilter({
          date: getDateRangeSinceDate(contact.createdAt, user?.timezone)
        });
      }
    };
    $[6] = contact;
    $[7] = setFilter;
    $[8] = user?.timezone;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = user?.timezone;
  let t5;
  if ($[10] !== contact || $[11] !== setFilter || $[12] !== t4) {
    t5 = [contact, setFilter, t4];
    $[10] = contact;
    $[11] = setFilter;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t3, t5);
  let t6;
  if ($[14] !== t) {
    t6 = t("global.source");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = survey => <div className="flex w-full flex-col">{survey.contactOptInHistoryItem.httpReferrer && <div className="flex w-full gap-1"><span className="whitespace-nowrap">{`${t("dashboard.contact_page.source_url")}: `}</span><a className="primary-link truncate outline-hidden" href={survey.contactOptInHistoryItem.httpReferrer} target="_blank" rel="noreferrer">{survey.contactOptInHistoryItem.httpReferrer}</a></div>}{survey.contactOptInHistoryItem.page && <div className="flex w-full gap-1"><span className="whitespace-nowrap">{`${t(pageTypeTitlesLocoKeys[survey.contactOptInHistoryItem.page.type])}: `}</span><a className="primary-link truncate outline-hidden" href={survey.contactOptInHistoryItem.page.editUrl}>{survey.contactOptInHistoryItem.page.name}</a></div>}</div>;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = {
      key: "contactOptInHistoryItem",
      header: t6,
      widthPercent: 40,
      onRender: t7
    };
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("dashboard.contact_page.survey_title");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = survey_0 => <div className="flex items-start justify-between gap-1"><div className="flex flex-col truncate">{hasSurveyContent(survey_0.content) && survey_0.content.map((el, idx) => <div key={idx} className={`flex flex-col gap-1 truncate py-3 ${idx !== 0 && "border-text-secondary/30 border-t"} ${idx === 0 && "pt-0"} ${idx + 1 === survey_0.content.length && "pb-0"}`}><Popover label={<div className="flex max-w-2xl flex-row gap-2">{el.userAnswers.map((answer, index) => answer.openAnswer ? <><div key={`${answer.title}-${index}`} className="flex flex-col items-start gap-1"><span className="text-text-primary font-bold">{t("dashboard.contact_page.survey_question")}</span><span key={index}>{answer.title}</span></div><div key={`${answer.openAnswer}-${index}`} className="flex flex-col items-start gap-1"><span className="text-text-primary font-bold">{t("dashboard.contact_page.survey_answers")}</span><span>{answer.openAnswer}</span></div></> : <div key={`${answer.title}-${index}`} className="flex flex-col items-start gap-1"><span className="text-text-primary font-bold">{t("dashboard.contact_page.survey_answers")}</span><span>{answer.title}</span></div>)}{el.tags.length !== 0 && <div className="flex flex-col items-start gap-1"><span className="text-text-primary font-bold">{t("dashboard.contact_page.tag")}</span>{el.tags.map(_temp)}</div>}</div>}>{open => <span className={`group-focus-visible:text-text-secondary text-start w-full whitespace-normal ${open ? "text-text-secondary" : "text-brand-primary"}`}>{el.title}</span>}</Popover></div>)}</div>{survey_0.contactOptInHistoryItem.optInBookingData && <Popover label={t("dashboard.contact_page.survey_booking_popover")}>{_temp2}</Popover>}</div>;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = {
      key: "content",
      header: t9,
      widthPercent: 60,
      onRender: t10
    };
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t8) {
    t12 = [t8, t11];
    $[28] = t11;
    $[29] = t8;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const columns = t12;
  let t13;
  if ($[31] !== t) {
    t13 = t("dashboard.contact_page.survey_title_plural");
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13) {
    t14 = <span className="text-[17px]">{t13}</span>;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = user?.timezone;
  let t16;
  if ($[35] !== t15) {
    t16 = getToday(t15);
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = contact?.createdAt;
  const t18 = user?.timezone;
  let t19;
  if ($[37] !== t17 || $[38] !== t18) {
    t19 = getDate(t17, t18);
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== contact?.createdAt || $[41] !== user?.timezone) {
    t20 = () => getDateRangeSinceDate(contact?.createdAt, user?.timezone);
    $[40] = contact?.createdAt;
    $[41] = user?.timezone;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== setFilter || $[44] !== setLimit || $[45] !== setPagination) {
    t21 = date => {
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      setLimit(10);
      setFilter({
        date
      });
    };
    $[43] = setFilter;
    $[44] = setLimit;
    $[45] = setPagination;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = user?.timezone;
  const t23 = user?.createdAt;
  let t24;
  if ($[47] !== filter.date || $[48] !== intlLocale || $[49] !== t16 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23) {
    t24 = <DateRangePicker maxDate={t16} minDate={t19} getDefaultDate={t20} date={filter.date} onChange={t21} timezone={t22} createdAt={t23} intlLocale={intlLocale} />;
    $[47] = filter.date;
    $[48] = intlLocale;
    $[49] = t16;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== limit || $[57] !== setLimit) {
    t25 = {
      limit,
      setLimit
    };
    $[56] = limit;
    $[57] = setLimit;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== pagination || $[60] !== setPagination) {
    t26 = {
      pagination,
      setPagination
    };
    $[59] = pagination;
    $[60] = setPagination;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== columns || $[63] !== contactSurveys || $[64] !== t25 || $[65] !== t26) {
    t27 = <Table limiter={t25} paginator={t26} data={contactSurveys} columns={columns} />;
    $[62] = columns;
    $[63] = contactSurveys;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== contactSurveys || $[68] !== t14 || $[69] !== t24 || $[70] !== t27) {
    t28 = <ListingLayout counter={false} data={contactSurveys} type="default" title={t14} customHeaderElement={t24}>{t27}</ListingLayout>;
    $[67] = contactSurveys;
    $[68] = t14;
    $[69] = t24;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  return t28;
};
export default ContactSurveys;
function _temp(tag, index_0) {
  return tag.id === null ? <span key={index_0}>{tag.name}</span> : <LinkWithoutPrefetch key={index_0} className="primary-link w-full" href={`/contacts?filter=%5B%7B"searchValue"%3A"${tag.id}"%2C"compareOperation"%3A"IN_LIST"%2C"searchField"%3A"tag"%7D%5D`}>{tag.name}</LinkWithoutPrefetch>;
}
function _temp2(open_0) {
  return <CalendarBookingIcon className={`h-4 w-4 ${open_0 ? "fill-brand-primary" : "fill-text-primary"} main-transition-colors`} />;
}